import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { QC_REJECTION_REASONS } from '@/lib/qcRejectionReasons';
import { PERMISSIONS } from '@/types/auth';
import type { JobWorkQcResultInput, JobWorkQcTaskDto } from '@/types/jobWork';
import { criteriaLabel, evaluateCheckpoint } from '@/utils/qcCheckpoint';

// Local draft shape while inspecting — passed stays null until the inspector actually decides it,
// so an untouched checkpoint is never silently sent as "Pass" — mirrors QualityControlPage.
interface QcResultDraft {
  checkpointId: string;
  passed: boolean | null;
  measuredValue: number | null;
  remarks: string | null;
}

// One reason+qty rejection line being built in the dialog — plus a client-only `id` for React
// keys/row identity (never sent to the server).
interface QcRejectionRow {
  id: string;
  reasonCode: string;
  qty: number;
}

/** Parallel to QualityControlPage, scoped to Job Work Receipt lines instead of GRN lines — the
 * whole-line inspection path only (no per-barcode units, since Job Work Label Print doesn't exist
 * yet). */
export default function JobWorkQcPage() {
  const { t } = useTranslation('jobWork');
  const queryClient = useQueryClient();

  const [tab, setTab] = useState(0);

  const { data: openTasks = [], isLoading: loadingOpen } = useQuery({
    queryKey: ['job-work-qc-tasks-pending'],
    queryFn: async () => (await apiClient.get<JobWorkQcTaskDto[]>('/job-work-qc-tasks/pending')).data,
    enabled: tab === 0,
  });
  const { data: completedTasks = [], isLoading: loadingCompleted } = useQuery({
    queryKey: ['job-work-qc-tasks-completed'],
    queryFn: async () => (await apiClient.get<JobWorkQcTaskDto[]>('/job-work-qc-tasks/completed', { params: { receiptLimit: 50 } })).data,
    enabled: tab === 1,
  });
  const tasks = tab === 0 ? openTasks : completedTasks;

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, QcResultDraft>>({});
  const [remarks, setRemarks] = useState('');
  const [rejectionRows, setRejectionRows] = useState<QcRejectionRow[]>([]);

  const selectedTask = tasks.find((t) => t.id === selectedTaskId) ?? null;
  const isFinalized = (status: string) => status === 'Passed' || status === 'Failed';
  const readOnly = selectedTask ? isFinalized(selectedTask.status) : false;
  const checkpointsWereRecorded = Boolean(selectedTask?.checkpoints.some((c) => c.passed !== null));
  const missingMandatoryCheckpoints = selectedTask
    ? selectedTask.checkpoints.filter((cp) => cp.isMandatory && (results[cp.checkpointId]?.passed ?? null) === null)
    : [];

  const totalRejectedQty = rejectionRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
  const acceptedQtyPreview = selectedTask ? selectedTask.receivedQty - totalRejectedQty : 0;
  const rejectedExceedsReceived = Boolean(selectedTask) && totalRejectedQty > selectedTask!.receivedQty;
  const rejectionRowsIncomplete = rejectionRows.some((r) => !r.reasonCode || r.qty <= 0);

  const openTask = (task: JobWorkQcTaskDto) => {
    setSelectedTaskId(task.id);
    setRemarks(task.remarks ?? '');
    setResults(
      Object.fromEntries(
        task.checkpoints.map((c) => [
          c.checkpointId,
          { checkpointId: c.checkpointId, passed: c.passed, measuredValue: c.measuredValue, remarks: c.remarks },
        ]),
      ),
    );
    setRejectionRows(task.rejections.map((r) => ({ id: r.id, reasonCode: r.reasonCode, qty: r.qty })));
  };

  const addRejectionRow = () => setRejectionRows((prev) => [...prev, { id: `row-${prev.length}-${Date.now()}`, reasonCode: '', qty: 0 }]);
  const updateRejectionRow = (id: string, patch: Partial<QcRejectionRow>) =>
    setRejectionRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRejectionRow = (id: string) => setRejectionRows((prev) => prev.filter((r) => r.id !== id));

  const decidedResults = (): JobWorkQcResultInput[] =>
    Object.values(results).flatMap((r) => (r.passed === null ? [] : [{ ...r, passed: r.passed }]));

  const closeDialog = () => setSelectedTaskId(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['job-work-qc-tasks-pending'] });
    queryClient.invalidateQueries({ queryKey: ['job-work-qc-tasks-completed'] });
    queryClient.invalidateQueries({ queryKey: ['job-work-putaway-tasks-pending'] });
    closeDialog();
  };

  const recordMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/job-work-qc-tasks/${selectedTaskId}/results`, { results: decidedResults(), remarks: remarks || null }),
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`/job-work-qc-tasks/${selectedTaskId}/results`, { results: decidedResults(), remarks: remarks || null });
      return apiClient.post(`/job-work-qc-tasks/${selectedTaskId}/submit`, {
        remarks: remarks || null,
        rejections: rejectionRows.filter((r) => r.qty > 0).map((r) => ({ reasonCode: r.reasonCode, qty: r.qty, remarks: null })),
      });
    },
    onSuccess: invalidate,
  });

  const holdMutation = useMutation({
    mutationFn: () => apiClient.post(`/job-work-qc-tasks/${selectedTaskId}/hold`, { remarks: remarks || null }),
    onSuccess: invalidate,
  });

  const reworkMutation = useMutation({
    mutationFn: () => apiClient.post(`/job-work-qc-tasks/${selectedTaskId}/rework`, { remarks: remarks || null }),
    onSuccess: invalidate,
  });

  const quickHoldMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/job-work-qc-tasks/${id}/hold`, { remarks: null }),
    onSuccess: invalidate,
  });

  const quickReworkMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/job-work-qc-tasks/${id}/rework`, { remarks: null }),
    onSuccess: invalidate,
  });

  const columns: DataTableColumn<JobWorkQcTaskDto>[] = [
    { field: 'jobWorkReceiptNumber', headerName: t('qc.table.receipt') },
    { field: 'materialName', headerName: t('qc.table.material'), render: (r) => `${r.materialCode} — ${r.materialName}` },
    { field: 'batchNo', headerName: t('qc.table.batch'), render: (r) => r.batchNo ?? '—' },
    { field: 'receivedQty', headerName: t('qc.table.qty') },
    { field: 'supplierName', headerName: t('qc.table.vendor'), filterable: true },
    { field: 'status', headerName: t('qc.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <IfPermitted permission={PERMISSIONS.JOB_WORK_EXECUTE}>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" variant="outlined" onClick={() => openTask(r)}>
              {isFinalized(r.status) ? t('qc.view') : t('qc.inspect')}
            </Button>
            {!isFinalized(r.status) && (
              <>
                <Button size="small" color="warning" disabled={quickHoldMutation.isPending} onClick={() => quickHoldMutation.mutate(r.id)}>
                  {t('qc.quickActions.hold')}
                </Button>
                <Button size="small" color="warning" disabled={quickReworkMutation.isPending} onClick={() => quickReworkMutation.mutate(r.id)}>
                  {t('qc.quickActions.rework')}
                </Button>
              </>
            )}
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('qc.title')} subtitle={t('qc.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t('qc.tabs.open')} />
        <Tab label={t('qc.tabs.completed')} />
      </Tabs>

      <DataTable
        columns={columns}
        rows={tasks}
        loading={tab === 0 ? loadingOpen : loadingCompleted}
        searchable
        getRowId={(r) => r.id}
        exportFileName={tab === 0 ? 'job-work-qc-open' : 'job-work-qc-completed'}
      />

      <Dialog open={Boolean(selectedTask)} onClose={closeDialog} maxWidth="sm" fullWidth>
        {selectedTask && (
          <>
            <DialogTitle>
              {selectedTask.materialCode} — {selectedTask.materialName}
              <Typography variant="body2" color="text.secondary">
                {t('qc.dialog.subtitle', { receipt: selectedTask.jobWorkReceiptNumber, batch: selectedTask.batchNo ?? '—' })}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                {!readOnly && missingMandatoryCheckpoints.length > 0 && (
                  <Alert severity="warning">
                    {t('qc.dialog.mandatoryCheckpointsRequired', { names: missingMandatoryCheckpoints.map((cp) => cp.checkpointName).join(', ') })}
                  </Alert>
                )}
                {selectedTask.checkpoints.length === 0 && (
                  <Typography color="text.secondary">{t('qc.dialog.noCheckpoints')}</Typography>
                )}
                {readOnly && selectedTask.checkpoints.length > 0 && !checkpointsWereRecorded && (
                  <Typography color="text.secondary">{t('qc.dialog.checkpointsNotRecorded')}</Typography>
                )}
                {(!readOnly || checkpointsWereRecorded) && selectedTask.checkpoints.map((cp) => {
                  const result = results[cp.checkpointId];
                  const isValueType = cp.valueType === 'Value';
                  const criteria = isValueType ? criteriaLabel(cp) : null;
                  return (
                    <Box key={cp.checkpointId}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2">{cp.checkpointName}</Typography>
                            {cp.isMandatory && <Chip label={t('qc.dialog.mandatory')} size="small" color="warning" />}
                          </Stack>
                          {cp.description && (
                            <Typography variant="caption" color="text.secondary">{cp.description}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t(`inspectionTypes.${cp.inspectionType}`, { ns: 'qcCheckpointMaster' })}
                            {criteria ? ` · ${t('qc.dialog.mustBe')} ${criteria}` : ''}
                          </Typography>
                        </Box>
                        <ToggleButtonGroup
                          size="small" exclusive value={result?.passed ?? null} disabled={readOnly}
                          onChange={(_, val) => {
                            if (val === null) return;
                            setResults((prev) => ({ ...prev, [cp.checkpointId]: { ...prev[cp.checkpointId], passed: val } }));
                          }}
                        >
                          <ToggleButton value={true} color="success">
                            <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> {t('qc.dialog.pass')}
                          </ToggleButton>
                          <ToggleButton value={false} color="error">
                            <CancelIcon fontSize="small" sx={{ mr: 0.5 }} /> {t('qc.dialog.fail')}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>
                      {isValueType && (
                        <TextField
                          type="number" label={t('qc.dialog.actualValue')} size="small" sx={{ mt: 1, minWidth: 160 }}
                          value={result?.measuredValue ?? ''}
                          disabled={readOnly}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const value = raw === '' ? null : Number(raw);
                            setResults((prev) => ({
                              ...prev,
                              [cp.checkpointId]: {
                                ...prev[cp.checkpointId],
                                measuredValue: value,
                                passed: value === null ? null : evaluateCheckpoint(value, cp),
                              },
                            }));
                          }}
                        />
                      )}
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  );
                })}

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">{t('qc.dialog.rejections')}</Typography>
                    {!readOnly && (
                      <Button size="small" startIcon={<AddIcon />} onClick={addRejectionRow}>
                        {t('qc.dialog.addRejection')}
                      </Button>
                    )}
                  </Stack>

                  {rejectionRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('qc.dialog.noRejections')}
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {rejectionRows.map((row) => (
                        <Stack key={row.id} direction="row" spacing={1} alignItems="center">
                          <SearchableSelect
                            label={t('qc.dialog.reason')}
                            value={row.reasonCode}
                            onChange={(value) => updateRejectionRow(row.id, { reasonCode: value })}
                            fullWidth
                            options={QC_REJECTION_REASONS}
                            disabled={readOnly}
                          />
                          <TextField
                            type="number" label={t('qc.dialog.rejectQty')} size="small" sx={{ width: 110 }}
                            value={row.qty} disabled={readOnly}
                            onChange={(e) => updateRejectionRow(row.id, { qty: Number(e.target.value) })}
                          />
                          {!readOnly && (
                            <IconButton size="small" onClick={() => removeRejectionRow(row.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  <Typography variant="caption" color={rejectedExceedsReceived ? 'error' : 'text.secondary'} display="block" sx={{ mt: 1 }}>
                    {t('qc.dialog.qtySummary', {
                      received: selectedTask.receivedQty, rejected: totalRejectedQty, accepted: Math.max(0, acceptedQtyPreview),
                    })}
                    {rejectedExceedsReceived && ` — ${t('qc.dialog.rejectedExceedsReceived')}`}
                  </Typography>
                </Box>

                <TextField
                  label={t('qc.dialog.remarks')} multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth
                  disabled={readOnly}
                />

                {selectedTask.status !== 'PendingQc' && <Chip label={selectedTask.status} size="small" sx={{ alignSelf: 'flex-start' }} />}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Button onClick={closeDialog}>{t('qc.cancel')}</Button>
              {readOnly ? null : (
                <>
                  <Button color="warning" onClick={() => holdMutation.mutate()} disabled={holdMutation.isPending}>
                    {t('qc.dialog.hold')}
                  </Button>
                  <Button color="warning" onClick={() => reworkMutation.mutate()} disabled={reworkMutation.isPending}>
                    {t('qc.dialog.rework')}
                  </Button>
                  <Button variant="outlined" onClick={() => recordMutation.mutate()} disabled={recordMutation.isPending}>
                    {t('qc.dialog.saveResults')}
                  </Button>
                  <Button
                    variant="contained" onClick={() => submitMutation.mutate()}
                    disabled={
                      submitMutation.isPending || missingMandatoryCheckpoints.length > 0 ||
                      rejectionRowsIncomplete || rejectedExceedsReceived
                    }
                  >
                    {t('qc.dialog.submit')}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
